import React, { type JSX } from 'react';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {Tooltip} from "@mui/material";
/**
 * @interface CustomButtonProps
 * @description Define las propiedades para el componente CustomButton.
 * @property {"submit" | "reset" | "button" | undefined} type - El tipo de botón HTML.
 * @property {string} text - El texto a mostrar dentro del botón.
 * @property {string} [className] - Clases CSS adicionales para aplicar al botón.
 * @property {boolean} [disabled] - Si es verdadero, el botón estará deshabilitado.
 * @property {boolean} [isLoading] - Si es verdadero, muestra un estado de carga y deshabilita el botón.
 * @property {() => void} [onClick] - Función de clic opcional para el botón.
 */
interface CustomButtonProps {
  type: 'submit' | 'reset' | 'button' | undefined;
  text?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  tooltip?: string;
  icon?: IconDefinition;
  onClick?: () => void;
}

/**
 * Un componente de botón personalizable y accesible con un estilo consistente.
 * Incluye estados de foco visible, deshabilitado y de carga.
 *
 * @component
 * @param {CustomButtonProps} props - Las propiedades para el componente.
 * @returns {JSX.Element} Un elemento de botón estilizado y accesible.
 */
export const CustomButton: React.FC<CustomButtonProps> = ({
  tooltip,
  type,
  text,
  className,
  onClick,
  disabled,
  isLoading,
  icon,
}: CustomButtonProps): JSX.Element => {
  const isDisabled = disabled || isLoading;

  return (
    <Tooltip
      title={tooltip || ''}
      placement="top"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
    >
      <button
        type={type}
        className={`rounded-xl cursor-pointer ${className ? className : ""}`}
        onClick={onClick}
        disabled={isDisabled}
        aria-busy={isLoading ? 'true' : 'false'}
      >
        {icon && <FontAwesomeIcon icon={icon} size="sm" />}
        {isLoading ? 'Cargando...' : text}
      </button>
    </Tooltip>
  );
};

export default CustomButton;
